import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useApp } from '@/contexts/AppContext';
import { useModals } from '@/contexts/ModalContext';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { z } from 'zod';
import { usePlanLimits } from '@/hooks/usePlanLimits';
import { FieldsBasicos } from './registrar-sessao/FieldsBasicos';
import { FieldsQuestoes } from './registrar-sessao/FieldsQuestoes';
import { FieldsRevisao } from './registrar-sessao/FieldsRevisao';
import { FieldsVideoAula } from './registrar-sessao/FieldsVideoAula';
import { AvisoConcluirConteudo } from './registrar-sessao/AvisoConcluirConteudo';

const videoSchema = z.object({
  videoUrl: z.string().trim().max(500).url({ message: 'URL inválida' }).optional().or(z.literal('')),
  inicio: z.number().int().min(0).optional(),
  fim: z.number().int().min(0).optional(),
}).refine(
  (data) => data.inicio === undefined || data.fim === undefined || data.fim >= data.inicio,
  { message: 'O minuto final deve ser maior ou igual ao inicial' }
);

export function RegistrarSessaoModal() {
  const { registrarOpen, closeRegistrar, registrarProps } = useModals();
  const { addSessao, getConteudoById, toggleConteudoConcluido } = useApp();
  const { podeCriarSessao, maxSessoesMes, planName, bloqueadas } = usePlanLimits();

  const [etapa, setEtapa] = useState<'form' | 'aviso'>('form');
  const [savedConteudoId, setSavedConteudoId] = useState<string>('');

  const [disciplinaId, setDisciplinaId] = useState('');
  const [conteudoId, setConteudoId] = useState('');
  const [tempo, setTempo] = useState('');
  const [data, setData] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [questoesCertas, setQuestoesCertas] = useState('');
  const [questoesErradas, setQuestoesErradas] = useState('');
  const [questoesBranco, setQuestoesBranco] = useState('');
  const [observacao, setObservacao] = useState('');
  const [criarRevisoes, setCriarRevisoes] = useState(true);
  const [tipoEstudo, setTipoEstudo] = useState('estudo_teorico');
  const [paginaInicio, setPaginaInicio] = useState('');
  const [paginaFim, setPaginaFim] = useState('');
  const [novoTipoEstudo, setNovoTipoEstudo] = useState('');
  const [showAddTipo, setShowAddTipo] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');
  const [videoMinutoInicio, setVideoMinutoInicio] = useState('');
  const [videoMinutoFim, setVideoMinutoFim] = useState('');

  const prevDisciplinaRef = useRef<string>('');

  useEffect(() => {
    if (registrarOpen) {
      const propDisc = registrarProps?.disciplinaId || '';
      const initialDisc = propDisc && !bloqueadas.has(propDisc) ? propDisc : '';
      setDisciplinaId(initialDisc);
      setConteudoId(initialDisc ? (registrarProps?.conteudoId || '') : '');
      prevDisciplinaRef.current = initialDisc;
      setTempo(registrarProps?.tempoPreenchido?.toString() || '');
      setData(format(new Date(), 'yyyy-MM-dd'));
      setQuestoesCertas('');
      setQuestoesErradas('');
      setQuestoesBranco('');
      setObservacao('');
      setCriarRevisoes(true);
      setTipoEstudo(registrarProps?.tipoEstudo || 'estudo_teorico');
      setPaginaInicio('');
      setPaginaFim('');
      setNovoTipoEstudo('');
      setShowAddTipo(false);
      setVideoUrl('');
      setVideoMinutoInicio('');
      setVideoMinutoFim('');
      setEtapa('form');
      setSavedConteudoId('');
    }
  }, [registrarOpen, registrarProps]);

  // Reset conteúdo apenas quando o usuário troca a disciplina manualmente
  useEffect(() => {
    if (!registrarOpen) return;
    if (prevDisciplinaRef.current !== disciplinaId) {
      setConteudoId('');
      prevDisciplinaRef.current = disciplinaId;
    }
  }, [disciplinaId, registrarOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!podeCriarSessao) {
      toast.error(`Limite de ${maxSessoesMes} sessões/mês atingido no plano ${planName}. Faça upgrade para registrar mais.`);
      return;
    }

    if (!disciplinaId || !conteudoId || !tempo || !data) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    if (bloqueadas.has(disciplinaId)) {
      toast.error(`Esta disciplina está bloqueada pelo plano ${planName}. Faça upgrade para registrá-la.`);
      return;
    }

    let videoUrlFinal: string | undefined;
    let videoInicioFinal: number | undefined;
    let videoFimFinal: number | undefined;

    if (tipoEstudo === 'video_aula') {
      if (!videoUrl.trim()) {
        toast.error('Informe a URL do vídeo aula');
        return;
      }
      const parsed = videoSchema.safeParse({
        videoUrl: videoUrl.trim(),
        inicio: videoMinutoInicio ? Number(videoMinutoInicio) : undefined,
        fim: videoMinutoFim ? Number(videoMinutoFim) : undefined,
      });
      if (!parsed.success) {
        toast.error(parsed.error.issues[0]?.message || 'Dados de vídeo inválidos');
        return;
      }
      videoUrlFinal = parsed.data.videoUrl || undefined;
      videoInicioFinal = parsed.data.inicio;
      videoFimFinal = parsed.data.fim;
    }

    const [year, month, day] = data.split('-').map(Number);
    const dataCorreta = new Date(year, month - 1, day);

    await addSessao({
      disciplinaId,
      conteudoId,
      tempoMin: Number(tempo),
      data: dataCorreta,
      questoesCertas: questoesCertas ? Number(questoesCertas) : undefined,
      questoesErradas: questoesErradas ? Number(questoesErradas) : undefined,
      questoesBranco: questoesBranco ? Number(questoesBranco) : undefined,
      observacao: observacao || undefined,
      tipoEstudo,
      paginaInicio: paginaInicio ? Number(paginaInicio) : undefined,
      paginaFim: paginaFim ? Number(paginaFim) : undefined,
      videoUrl: videoUrlFinal,
      videoMinutoInicio: videoInicioFinal,
      videoMinutoFim: videoFimFinal,
    }, criarRevisoes);

    toast.success(criarRevisoes
      ? 'Sessão registrada com revisões agendadas!'
      : 'Sessão registrada sem revisões!');

    const conteudoSelecionado = getConteudoById(conteudoId);
    if (conteudoSelecionado && !conteudoSelecionado.revisaoConcluida) {
      setSavedConteudoId(conteudoId);
      setEtapa('aviso');
    } else {
      closeRegistrar();
    }
  };

  const handleConcluirConteudo = async () => {
    if (savedConteudoId) {
      await toggleConteudoConcluido(savedConteudoId);
      toast.success('Conteúdo marcado como concluído!');
    }
    closeRegistrar();
  };

  const conteudoSalvo = savedConteudoId ? getConteudoById(savedConteudoId) : undefined;

  return (
    <Dialog open={registrarOpen} onOpenChange={(open) => !open && closeRegistrar()}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">
            {etapa === 'form' ? 'Registrar Sessão' : 'Tudo certo!'}
          </DialogTitle>
        </DialogHeader>

        {etapa === 'form' ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <FieldsBasicos
              disciplinaId={disciplinaId} setDisciplinaId={setDisciplinaId}
              conteudoId={conteudoId} setConteudoId={setConteudoId}
              tipoEstudo={tipoEstudo} setTipoEstudo={setTipoEstudo}
              tempo={tempo} setTempo={setTempo}
              data={data} setData={setData}
              paginaInicio={paginaInicio} setPaginaInicio={setPaginaInicio}
              paginaFim={paginaFim} setPaginaFim={setPaginaFim}
              showAddTipo={showAddTipo} setShowAddTipo={setShowAddTipo}
              novoTipoEstudo={novoTipoEstudo} setNovoTipoEstudo={setNovoTipoEstudo}
            />

            {tipoEstudo === 'video_aula' && (
              <FieldsVideoAula
                videoUrl={videoUrl} setVideoUrl={setVideoUrl}
                videoMinutoInicio={videoMinutoInicio} setVideoMinutoInicio={setVideoMinutoInicio}
                videoMinutoFim={videoMinutoFim} setVideoMinutoFim={setVideoMinutoFim}
              />
            )}

            <FieldsQuestoes
              questoesCertas={questoesCertas} setQuestoesCertas={setQuestoesCertas}
              questoesErradas={questoesErradas} setQuestoesErradas={setQuestoesErradas}
              questoesBranco={questoesBranco} setQuestoesBranco={setQuestoesBranco}
              observacao={observacao} setObservacao={setObservacao}
            />

            <FieldsRevisao criarRevisoes={criarRevisoes} setCriarRevisoes={setCriarRevisoes} />

            <Button type="submit" className="w-full" size="lg">
              Salvar Sessão
            </Button>
          </form>
        ) : (
          <AvisoConcluirConteudo
            conteudoNome={conteudoSalvo?.nome || 'este conteúdo'}
            onConcluir={handleConcluirConteudo}
            onPular={closeRegistrar}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
